import { useEffect, useId, useState, type ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Check, Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import SearchSelect from '@/components/ui/search-select';
import StoreIcon from '@/components/common/StoreIcon';
import {
  IJobType,
  IFlipDirection,
  IZZeroPosition,
  ISoftwareUsed,
  IWood,
  IMetals,
  IPlastic,
  IOther,
} from '@/redux/post/post.d';
import {
  selectV2,
  setStep,
  setBasics,
  setJobDetails,
  seedJobDetailsFromParser,
} from '../v2Upload.slice';
import {
  FIELD_LABEL,
  OPTION_KEY,
  STEP_HEADING,
} from '../components/fileCardStyles';
import {
  FlipArt,
  JobTypeArt,
  RotaryStockDiagram,
  StockDiagram,
  XYDatumBoard,
  ZZeroArt,
} from '../components/SetupDiagrams';
import SectionHelp from '../components/SectionHelp';
import AutofillMark, { AutofillLegend } from '../components/AutofillMark';
import { autofillState, AutofillState } from '../utils/autofill';
import { jobDetailAnswers } from '../utils/mergeSources';
import SoftwareChip from '../components/SoftwareChip';
import WizardFooter from '../components/WizardFooter';
import FieldError, {
  redlineControl,
  redlineGroup,
} from '../components/FieldError';
import useFieldErrors, { FieldErrors } from '../useFieldErrors';
import { plural } from '../utils/fileHelpers';
import { missingForSubmit, Missing } from '../utils/validation';
import useDraftSync from '../useDraftSync';
import { V2JobDetails } from '../v2Upload.d';
import type { GcodeExtractedData } from '@/workers/gcodeParser';

const SECTION = 'font-secondary text-base font-semibold text-foreground';

const SOFTWARE = Object.values(ISoftwareUsed);

const MATERIALS = [
  ...Object.values(IWood),
  ...Object.values(IMetals),
  ...Object.values(IPlastic),
  ...Object.values(IOther),
];

/** One numbered card of the step: the badge, its title, and the question it
 * answers. */
function Section({
  n,
  title,
  hint,
  optional,
  focus,
  children,
}: {
  n: number;
  title: string;
  hint: string;
  optional?: boolean;
  focus?: string;
  children: ReactNode;
}) {
  return (
    <section
      className="rounded-xl border bg-card p-5 shadow-md transition-[border-color,box-shadow] duration-fast focus-within:border-brand focus-within:shadow-lg"
      data-focus={focus}
    >
      <div className="mb-4 flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-100 text-xs font-semibold text-green-800">
          {n}
        </span>
        <h3 className={SECTION}>{title}</h3>
        <SectionHelp title={title} body={hint} />
        {optional && (
          <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] font-tertiary text-neutral-700">
            Optional
          </span>
        )}
      </div>
      {children}
    </section>
  );
}
/** A radio option as an illustrated tile: the drawing, then the enum's own
 * wording. The `<label>` is what makes the whole tile clickable and what names
 * the radio — the drawing is decorative and must stay out of that name, which
 * is what the E2E suite picks options by. */
function RadioCard({
  selected,
  value,
  art,
}: {
  selected: boolean;
  value: string;
  art: ReactNode;
}) {
  const id = useId();
  return (
    <label
      htmlFor={id}
      className={cn(
        'relative flex cursor-pointer flex-col items-center gap-1.5 rounded-xl border p-3 text-center transition-colors duration-fast',
        selected
          ? 'border-primary bg-accent text-primary'
          : 'border-border hover:bg-muted/40'
      )}
    >
      {selected && (
        <Check className="absolute right-1.5 top-1.5 h-4 w-4 rounded-full bg-primary p-0.5 text-primary-foreground" />
      )}
      {art}
      <span className={OPTION_KEY}>{value}</span>
      {/* The whole tile IS the radio: invisible but full-size, so a click (and
          Playwright's hit test) lands on the control itself. */}
      <RadioGroupItem
        id={id}
        value={value}
        className="absolute inset-0 h-full w-full cursor-pointer rounded-xl border-0 opacity-0"
      />
    </label>
  );
}

/** An optional machine feature as a selectable tile — on/off is the tile's own
 * state, the way the mockup shows it. */
function FeatureTile({
  focus,
  label,
  art,
  value,
  onChange,
  mark,
}: {
  focus: string;
  label: string;
  art: ReactNode;
  value: boolean;
  onChange: (v: boolean) => void;
  mark: AutofillState;
}) {
  return (
    <div data-focus={focus}>
      <button
        type="button"
        role="checkbox"
        aria-checked={value}
        aria-label={label}
        onClick={() => onChange(!value)}
        className={cn(
          'relative flex w-full items-center gap-3 rounded-xl border p-4 pr-10 text-left transition-colors duration-fast',
          value
            ? 'border-primary bg-accent text-primary'
            : 'border-border hover:bg-muted/40'
        )}
      >
        {value && (
          <Check className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-primary p-0.5 text-primary-foreground" />
        )}
        {art}
        <span className={OPTION_KEY}>{label}</span>
        <AutofillMark state={mark} />
      </button>
    </div>
  );
}

/** Right-aligned number field with the unit pinned inside, like the mockup. */
function SizeInput({
  label,
  value,
  unit,
  onChange,
  invalid,
}: {
  label: string;
  value: string;
  unit: string;
  onChange: (v: string) => void;
  invalid?: boolean;
}) {
  return (
    <div>
      <span
        className={cn(
          FIELD_LABEL,
          'mb-1.5 block truncate text-[10px] tracking-normal'
        )}
        title={label}
      >
        {label}
      </span>
      <div className="relative">
        <Input
          type="number"
          inputMode="decimal"
          min={0}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={cn(
            'pr-8 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
            redlineControl(invalid ?? false)
          )}
        />
        <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-xs font-tertiary text-info">
          {unit}
        </span>
      </div>
    </div>
  );
}

/** Which unit the sizes are in. Read-only — it comes out of the G-code, and
 * Sign-off greys its review cell for the same reason. */
function UnitPill({ units }: { units: string }) {
  return (
    <div
      className="inline-flex gap-1 rounded-full border p-0.5"
      title="Read from your G-code"
    >
      {['Inches', 'MM'].map((u) => (
        <span
          key={u}
          className={cn(
            'rounded-full px-2.5 py-0.5 text-[11px] font-tertiary',
            units === u
              ? 'bg-primary/10 text-primary font-medium'
              : 'text-muted-foreground'
          )}
        >
          {u === 'Inches' ? 'inches' : 'mm'}
        </span>
      ))}
    </div>
  );
}

/** The touch-probe and tool-changer glyphs, sized for a FeatureTile. */
function ToolSetterArt() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className="h-6 w-6 flex-none">
      <path
        d="M12 3v7m0 0-3.5 5.5h7L12 10Zm-6 13h12"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function AtcArt() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className="h-6 w-6 flex-none">
      <path
        d="M4 4h16M8 4v9a4 4 0 0 0 8 0V4M12 17v3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** The URL list for a dimensional material: a removable row per link, plus an
 * input that adds on Enter or the button. */
function MaterialLinks({
  links,
  draft,
  onDraftChange,
  onAdd,
  onRemove,
}: {
  links: string[];
  draft: string;
  onDraftChange: (v: string) => void;
  onAdd: () => void;
  onRemove: (url: string) => void;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className={cn(FIELD_LABEL, 'block')}>Product Link (URL)</span>
        <span className="text-[11px] font-tertiary text-muted-foreground">
          {links.length} {plural(links.length, 'link', 'links')}
        </span>
      </div>
      {links.length > 0 && (
        <div className="space-y-2 mb-2">
          {links.map((url) => (
            <div
              key={url}
              className="flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2"
            >
              <StoreIcon url={url} className="h-6 w-6 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate text-sm" title={url}>
                {url}
              </span>
              <button
                type="button"
                onClick={() => onRemove(url)}
                aria-label="Remove link"
                className="shrink-0 text-muted-foreground transition-colors hover:text-destructive"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Input
          type="url"
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onAdd();
            }
          }}
          placeholder="https://www.lowes.com/pd/Walnut-Board-3…"
          className="flex-1"
        />
        <Button type="button" onClick={onAdd} disabled={!draft.trim()}>
          Add link
        </Button>
      </div>
    </div>
  );
}

const unitLabel = (units: string): string => (units === 'Inches' ? 'in' : 'mm');

/** A cylinder is zeroed on its surface or its axis; a flat job on the stock or
 * the bed. Offering all four lets a rotary job be submitted zeroed to a machine
 * bed it never touches. */
const zZeroOptions = (jobType: string): IZZeroPosition[] =>
  jobType === IJobType.ROTARY
    ? [IZZeroPosition.CYLINDER_SURFACE, IZZeroPosition.CYLINDER_AXIS]
    : [IZZeroPosition.MATERIAL_SURFACE, IZZeroPosition.MACHINE_BED];

/** Job size. Rotary measures a cylinder, so it asks for length and diameter
 * instead of a bounding box — matching the `{length, diameter}` shape
 * createPostDto requires for that job type, and v1's own jobSizeLength /
 * jobSizeDiameter fields. */
function JobSize({
  jd,
  set,
  missing,
  fields,
  mark,
}: {
  jd: V2JobDetails;
  set: (patch: Partial<V2JobDetails>) => void;
  missing: Missing[];
  fields: FieldErrors;
  mark: AutofillState;
}) {
  const unit = unitLabel(jd.units);
  const isRotary = jd.jobType === IJobType.ROTARY;

  return (
    <div {...fields.props('jobSize')}>
      <div className="mb-3 flex h-[22px] items-center justify-between gap-3">
        <span className={cn(FIELD_LABEL, 'flex items-center gap-1.5')}>
          Material Dimensions
          <AutofillMark state={mark} />
        </span>
        <UnitPill units={jd.units} />
      </div>
      {isRotary ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SizeInput
            label="Length"
            unit={unit}
            value={jd.length}
            onChange={(v) => set({ length: v })}
            invalid={fields.bad('jobSize')}
          />
          <SizeInput
            label="Diameter"
            unit={unit}
            value={jd.diameter}
            onChange={(v) => set({ diameter: v })}
            invalid={fields.bad('jobSize')}
          />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <SizeInput
              label="Width (X)"
              unit={unit}
              value={jd.width}
              onChange={(v) => set({ width: v })}
              invalid={fields.bad('jobSize')}
            />
            <SizeInput
              label="Height (Y)"
              unit={unit}
              value={jd.height}
              onChange={(v) => set({ height: v })}
              invalid={fields.bad('jobSize')}
            />
            <SizeInput
              label="Thickness (Z)"
              unit={unit}
              value={jd.thickness}
              onChange={(v) => set({ thickness: v })}
              invalid={fields.bad('jobSize')}
            />
          </div>
        </div>
      )}
      {isRotary && (
        <p className="text-xs text-muted-foreground mt-2">
          Read from your G-code for flat jobs, but a file can&apos;t say how
          thick the cylinder is — these two are yours to enter.
        </p>
      )}
      <FieldError
        missing={missing}
        field="jobSize"
        show={fields.bad('jobSize')}
      />
    </div>
  );
}

const NOTHING_PICKED = 'Nothing picked yet';

/** The stock as it will actually sit on the bed — a cylinder for a rotary job,
 * a block for everything else. */
function StockArt({ jd, material }: { jd: V2JobDetails; material: string }) {
  if (jd.jobType === IJobType.ROTARY) {
    return (
      <RotaryStockDiagram
        material={material}
        dims={{
          length: jd.length,
          diameter: jd.diameter,
          unit: unitLabel(jd.units),
        }}
      />
    );
  }
  return (
    <StockDiagram
      material={material}
      dims={{
        x: jd.width,
        y: jd.height,
        z: jd.thickness,
        unit: unitLabel(jd.units),
      }}
    />
  );
}

/** The way out to the full list, shown only while it is collapsed. */
function AddSoftwareButton({
  show,
  onClick,
}: {
  show: boolean;
  onClick: () => void;
}) {
  if (!show) return null;
  return (
    <Button variant="outline" size="sm" onClick={onClick}>
      <Plus className="mr-1 h-3.5 w-3.5" /> Add software
    </Button>
  );
}

/** Only what is picked, until asked otherwise: the parser usually names the CAM
 * software already, and a twelve-chip grid to confirm one answer is a wall. */
const softwareChips = (all: boolean, picked: string[]) =>
  all ? SOFTWARE : (picked as ISoftwareUsed[]);

/**
 * Switching to (or off) Rotary swaps which Z-zero options exist, so a pick that
 * no longer applies is dropped rather than submitted invisibly.
 */
function jobTypePatch(
  jobType: IJobType,
  zZeroPosition: string
): Partial<V2JobDetails> {
  const stillOffered = zZeroOptions(jobType).includes(
    zZeroPosition as IZZeroPosition
  );
  return stillOffered ? { jobType } : { jobType, zZeroPosition: '' };
}

/** `value` added to the list, or taken out of it. */
const toggled = (list: string[], value: string) =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

/** Seed the form from the parser, once. The reducer's `seeded` guard keeps
 * every edit the user makes after the first fill. */
function useParserSeeding(extracted: GcodeExtractedData | null) {
  const dispatch = useDispatch();
  useEffect(() => {
    if (extracted) dispatch(seedJobDetailsFromParser(extracted));
  }, [extracted, dispatch]);
}

/**
 * The product links for a dimensional material.
 *
 * The section has no yes/no of its own — a link IS the answer, so the flag the
 * backend stores follows the list.
 */
function useMaterialLinks(
  links: string[],
  set: (patch: Partial<V2JobDetails>) => void
) {
  const [draft, setDraft] = useState('');

  const write = (next: string[]) =>
    set({
      dimensionalMaterialLinks: next,
      isDimensionalMaterial: next.length > 0,
    });

  const add = () => {
    const url = draft.trim();
    if (!url || links.includes(url)) return;
    write([...links, url]);
    setDraft('');
  };

  return {
    draft,
    setDraft,
    add,
    remove: (url: string) => write(links.filter((l) => l !== url)),
  };
}

export default function JobDetailsStep() {
  const dispatch = useDispatch();
  const { basics, jobDetails, extractedData, camExtracted, files } =
    useSelector(selectV2);
  const { syncJobDetails } = useDraftSync();
  const [saving, setSaving] = useState(false);
  // Open from the start when there is nothing picked yet — a collapsed empty
  // list would be a required field with no visible way to answer it.
  const [allSoftware, setAllSoftware] = useState(
    jobDetails.softwareUsed.length === 0
  );
  useParserSeeding(extractedData);

  const set = (patch: Partial<V2JobDetails>) => dispatch(setJobDetails(patch));
  const answers = jobDetailAnswers(extractedData, camExtracted, files);
  const mark = (...keys: Array<keyof V2JobDetails>): AutofillState =>
    autofillState(
      answers,
      keys.map((key) => [key, jobDetails[key]])
    );
  const missing = missingForSubmit(jobDetails);
  const fields = useFieldErrors(missing);
  const { bad, props } = fields;
  const isDoubleSided = jobDetails.jobType === IJobType.DOUBLE_SIDED;

  const setJobType = (jobType: IJobType) =>
    set(jobTypePatch(jobType, jobDetails.zZeroPosition));

  const toggleSoftware = (value: string) =>
    set({ softwareUsed: toggled(jobDetails.softwareUsed, value) });

  const links = useMaterialLinks(jobDetails.dimensionalMaterialLinks, set);

  const handleContinue = async () => {
    if (!fields.guard()) return;
    setSaving(true);
    const ok = await syncJobDetails();
    setSaving(false);
    if (ok) dispatch(setStep(5));
  };

  return (
    <div className="flex flex-col min-h-full">
      <h2 className={STEP_HEADING}>Tell makers how to set up this project</h2>
      <p className="text-sm text-muted-foreground mb-3 max-w-[640px]">
        Add the materials, size, machine setup, and project information makers
        need before they begin.
      </p>
      <AutofillLegend show={Object.values(answers).some(Boolean)} />

      {/* Material and its links on the left, machine setup on the right. */}
      <div className="grid max-w-[1400px] grid-cols-1 items-start gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          <Section
            n={1}
            title="Material & Dimensions"
            hint="What is the project made from?"
            focus="material-section"
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-[minmax(0,0.55fr)_minmax(0,1fr)]">
              <div data-focus="material">
                <div className="mb-3 flex h-[22px] items-center">
                  <span className={cn(FIELD_LABEL, 'block')}>
                    Primary Material
                  </span>
                </div>
                {/* Stands in for the Width/Height/Thickness label row, so the
                    select sits on the same line as the number fields. */}
                <span className={cn(FIELD_LABEL, 'mb-1.5 block')}>&nbsp;</span>
                <SearchSelect
                  aria-label="Material"
                  value={basics.material}
                  options={[...MATERIALS]}
                  onChange={(v) => dispatch(setBasics({ material: v }))}
                />
              </div>
              <JobSize
                jd={jobDetails}
                set={set}
                missing={missing}
                fields={fields}
                mark={mark('width', 'height', 'thickness')}
              />
            </div>
            <StockArt jd={jobDetails} material={basics.material} />
          </Section>

          <Section
            n={5}
            title="Optional Big Box Store Lumber"
            hint="Link to the board or sheet good used for this project so makers can buy the right material."
            optional
            focus="links-section"
          >
            <p className="-mt-3 mb-4 text-xs text-muted-foreground">
              Add a product link from a trusted retailer or the supplier you
              used to source this material for this project.
            </p>
            <MaterialLinks
              links={jobDetails.dimensionalMaterialLinks}
              draft={links.draft}
              onDraftChange={links.setDraft}
              onAdd={links.add}
              onRemove={links.remove}
            />
          </Section>
        </div>

        <div className="space-y-6">
          <Section
            n={2}
            title="Project Setup"
            hint="How will this project be cut and where is it zeroed?"
            focus="setup-section"
          >
            {/* Project type decides which inputs Material Dimensions renders —
                width/height/thickness, or length/diameter for Rotary. */}
            <div {...props('jobType')} className="mb-5">
              <div
                className={cn(FIELD_LABEL, 'mb-3 flex items-center gap-1.5')}
              >
                Project Type
                <AutofillMark state={mark('jobType')} />
              </div>
              <RadioGroup
                value={jobDetails.jobType}
                onValueChange={(v) => setJobType(v as IJobType)}
                className={cn(
                  'grid grid-cols-2 gap-3 sm:grid-cols-4',
                  redlineGroup(bad('jobType'))
                )}
              >
                {Object.values(IJobType).map((v) => (
                  <RadioCard
                    key={v}
                    selected={jobDetails.jobType === v}
                    value={v}
                    art={<JobTypeArt type={v} />}
                  />
                ))}
              </RadioGroup>
              <FieldError
                missing={missing}
                field="jobType"
                show={bad('jobType')}
              />
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div {...props('zZero')}>
                <div
                  className={cn(FIELD_LABEL, 'mb-3 flex items-center gap-1.5')}
                >
                  Z Zero (Vertical Origin)
                  <AutofillMark state={mark('zZeroPosition')} />
                </div>
                <RadioGroup
                  value={jobDetails.zZeroPosition}
                  onValueChange={(v) =>
                    set({ zZeroPosition: v as IZZeroPosition })
                  }
                  className={cn(
                    'grid grid-cols-2 gap-3',
                    redlineGroup(bad('zZero'))
                  )}
                >
                  {zZeroOptions(jobDetails.jobType).map((v) => (
                    <RadioCard
                      key={v}
                      selected={jobDetails.zZeroPosition === v}
                      value={v}
                      art={<ZZeroArt value={v} />}
                    />
                  ))}
                </RadioGroup>
                <FieldError
                  missing={missing}
                  field="zZero"
                  show={bad('zZero')}
                />
              </div>

              <div {...props('xyDatum')}>
                <div
                  className={cn(FIELD_LABEL, 'mb-3 flex items-center gap-1.5')}
                >
                  XY Origin (Horizontal Origin)
                  <AutofillMark state={mark('xyDatumPosition')} />
                </div>
                <div
                  className={cn(
                    'flex w-fit items-center gap-5',
                    redlineGroup(bad('xyDatum'))
                  )}
                >
                  <XYDatumBoard
                    value={jobDetails.xyDatumPosition}
                    onChange={(v) => set({ xyDatumPosition: v })}
                  />
                  <span className="text-xs text-muted-foreground">
                    {jobDetails.xyDatumPosition || NOTHING_PICKED}
                  </span>
                </div>
                <FieldError
                  missing={missing}
                  field="xyDatum"
                  show={bad('xyDatum')}
                />
              </div>
            </div>

            {/* Flip direction — only asked for, and only accepted, on a double
            sided job. Which way the board is turned over between the two
            operations is not in the G-code, and the submit is rejected without
            it. */}
            {isDoubleSided && (
              <div {...props('flipDirection')} className="mt-5">
                <div className={cn(FIELD_LABEL, 'mb-3 block')}>
                  Flip direction
                </div>
                <RadioGroup
                  value={jobDetails.flipDirection}
                  onValueChange={(v) => set({ flipDirection: v })}
                  className={cn(
                    'flex w-fit gap-3',
                    redlineGroup(bad('flipDirection'))
                  )}
                >
                  {Object.values(IFlipDirection).map((v) => (
                    <RadioCard
                      key={v}
                      selected={jobDetails.flipDirection === v}
                      value={v}
                      art={<FlipArt direction={v} />}
                    />
                  ))}
                </RadioGroup>
                <FieldError
                  missing={missing}
                  field="flipDirection"
                  show={bad('flipDirection')}
                />
              </div>
            )}
          </Section>

          {/* Design software — multi-select over the fixed ISoftwareUsed list,
              same as v1. Checkbox pills so it reads as "pick any". */}
          <Section
            n={3}
            title="Software & Machine"
            hint="What software was this project designed in?"
            focus="software-section"
          >
            <div {...props('software')}>
              <div className="mb-3 flex items-center justify-between gap-3">
                <span className={cn(FIELD_LABEL, 'flex items-center gap-1.5')}>
                  Design Software (select one or more)
                  <AutofillMark state={mark('softwareUsed')} />
                </span>
                <AddSoftwareButton
                  show={!allSoftware}
                  onClick={() => setAllSoftware(true)}
                />
              </div>
              {/* Only what is picked, until asked otherwise: the parser usually
                  names the CAM software already, and a twelve-chip grid to
                  confirm one answer is a wall. */}
              <div
                className={cn(
                  'grid grid-cols-2 gap-2 sm:grid-cols-3',
                  redlineGroup(bad('software'))
                )}
              >
                {softwareChips(allSoftware, jobDetails.softwareUsed).map(
                  (v) => (
                    <SoftwareChip
                      key={v}
                      value={v}
                      active={jobDetails.softwareUsed.includes(v)}
                      onToggle={() => toggleSoftware(v)}
                    />
                  )
                )}
              </div>
              <FieldError
                missing={missing}
                field="software"
                show={bad('software')}
              />
            </div>
          </Section>

          {/* `atc` is persisted as probeForZHeightCommand — v1's "Automatic
              Tool Changer (ATC)" field, seeded from the parser's detection and
              still editable. */}
          <Section
            n={4}
            title="Optional Machine Features"
            hint="Select any special features or setups used for this project."
            focus="features-section"
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FeatureTile
                focus="toolSetter"
                label="Tool Setter"
                art={<ToolSetterArt />}
                value={jobDetails.toolSetter}
                onChange={(v) => set({ toolSetter: v })}
                mark={mark('toolSetter')}
              />
              <FeatureTile
                focus="atc"
                label="Automatic Tool Changer (ATC)"
                art={<AtcArt />}
                value={jobDetails.autoToolChanger}
                onChange={(v) => set({ autoToolChanger: v })}
                mark={mark('autoToolChanger')}
              />
            </div>
          </Section>
        </div>
      </div>

      <WizardFooter
        onBack={() => dispatch(setStep(3))}
        onContinue={handleContinue}
        busy={saving}
        busyLabel="Saving…"
        backDisabled={saving}
        continueDisabled={false}
      />
    </div>
  );
}
